import { LiveData, Service } from '@toeverything/infra';
import { map } from 'rxjs';

import {
  canvasOverviewShortcutConflicts,
  DEFAULT_CANVAS_OVERVIEW_SHORTCUT,
  normalizeCanvasOverviewShortcut,
} from '../../../commands/canvas-overview-shortcut';
import {
  DEFAULT_SELECTOR_SHORTCUT,
  normalizeSelectorShortcut,
  selectorShortcutConflicts,
} from '../../../commands/selector-shortcut';
import type {
  GlobalCache,
  GlobalSessionState,
  GlobalState,
} from '../providers/global';

export type ShortcutSaveResult =
  | { ok: true }
  | { ok: false; reason: 'invalid' | 'conflict' };

export class GlobalStateService extends Service {
  constructor(public readonly globalState: GlobalState) {
    super();
  }

  readonly selectorShortcut$ = LiveData.from(
    this.globalState
      .watch<string | null>('copilot.selectorShortcut')
      .pipe(
        map(value =>
          value === undefined
            ? DEFAULT_SELECTOR_SHORTCUT
            : value === null
              ? null
              : normalizeSelectorShortcut(value)
        )
      ),
    DEFAULT_SELECTOR_SHORTCUT as string | null
  );

  setSelectorShortcut(
    binding: string | null,
    nativeBindings: Iterable<string> = []
  ): ShortcutSaveResult {
    const normalized =
      binding === null ? null : normalizeSelectorShortcut(binding);
    if (binding !== null && !normalized)
      return { ok: false, reason: 'invalid' };
    if (normalized && selectorShortcutConflicts(normalized, nativeBindings))
      return { ok: false, reason: 'conflict' };
    this.globalState.set('copilot.selectorShortcut', normalized);
    return { ok: true };
  }

  resetSelectorShortcut() {
    return this.setSelectorShortcut(DEFAULT_SELECTOR_SHORTCUT);
  }

  readonly canvasOverviewShortcut$ = LiveData.from(
    this.globalState
      .watch<string | null>('edgeless.canvasOverviewShortcut')
      .pipe(
        map(value =>
          value === undefined
            ? DEFAULT_CANVAS_OVERVIEW_SHORTCUT
            : value === null
              ? null
              : normalizeCanvasOverviewShortcut(value)
        )
      ),
    DEFAULT_CANVAS_OVERVIEW_SHORTCUT as string | null
  );

  setCanvasOverviewShortcut(
    binding: string | null,
    nativeBindings: Iterable<string> = []
  ): ShortcutSaveResult {
    const normalized =
      binding === null ? null : normalizeCanvasOverviewShortcut(binding);
    if (binding !== null && !normalized)
      return { ok: false, reason: 'invalid' };
    if (
      normalized &&
      canvasOverviewShortcutConflicts(normalized, nativeBindings)
    )
      return { ok: false, reason: 'conflict' };
    this.globalState.set('edgeless.canvasOverviewShortcut', normalized);
    return { ok: true };
  }
}

export class GlobalCacheService extends Service {
  constructor(public readonly globalCache: GlobalCache) {
    super();
  }
}

export class GlobalSessionStateService extends Service {
  constructor(public readonly globalSessionState: GlobalSessionState) {
    super();
  }
}
